import React, { FC, MouseEvent, useEffect, useRef, useState } from "react";
import makeStyles from "@mui/styles/makeStyles";
import { AlertIconListViewConfig } from "components/portfolio/components/alert/alert-icon-type";
import { Skeleton } from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { IconButton, Tooltip } from "@mui/material";
import useSWR from "swr";
import moment from "moment/moment";
import ArrowPopper from "components/app/ArrowPopper";
import { AnchorHistory, CoverageStatusHistory } from "types/portfolio";
import { classHOC } from "components/app/ClassWrapper";

const useStyles = makeStyles(() => ({
  listContainer: {
    maxHeight: "52vh",
    overflowY: "auto",
    "&::-webkit-scrollbar": {
      width: 4,
    },
    paddingRight: 2,
  },
  cardContainer: {
    borderRadius: 6,
    border: "1px solid #DEDEDE",
    marginTop: 8,
    "&:first-of-type": {
      marginTop: 0,
    },
    paddingBottom: 8,
  },
  itemContainer: {
    display: "flex",
    width: "100%",
    marginTop: 8,
    paddingLeft: 12,
  },
  itemLabel: {
    fontSize: 12,
    color: "#898989",
    lineHeight: "16px",
    width: 100,
    display: "flex",
    alignItems: "center",
  },
  itemValue: {
    fontSize: 12,
    lineHeight: "16px",
    color: "#333333",
    display: "flex",
    alignItems: "center",
    textOverflow: "ellipsis",
    width: "12vw",
  },
  itemActionContainer: {
    display: "flex",
    justifyContent: "space-between",
    borderTop: "1px solid #EDEDED",
    padding: "6px 12px",
    marginTop: 12,
  },
  action: {
    fontSize: 12,
    color: "#00A0D1",
    cursor: "pointer",
    "&:hover": {
      textDecoration: "underline",
    },
  },
  tooltip: {
    maxHeight: "30vh",
  },
  iconStyle: {
    fontSize: "12px",
    color: "#B2B2B2",
    marginLeft: "4px",
  },
}));

const HistoryList: FC<{
  accountKey?: string;
  historyInfo?: {
    label: string;
    field: keyof AnchorHistory & CoverageStatusHistory;
    apiKey: string;
    valueFormat: boolean;
    labelComment?: string;
  };
}> = ({
  accountKey = "",
  historyInfo = {
    label: "Anchor Product(s)",
    field: "anchorProducts",
    apiKey: "anchor-products",
    valueFormat: true,
    labelComment: "Reason",
  },
}) => {
  const configs: AlertIconListViewConfig<AnchorHistory & CoverageStatusHistory>[] = [
    {
      label: historyInfo?.label,
      colId: historyInfo?.field,
      valueFormatter: (_, data) =>
        historyInfo?.valueFormat
          ? `${data.anchorProducts
              .map((x) => {
                const [type, value] = x.split("%-%");
                return value ? `Other - ${value}` : type;
              })
              .join(", ")}`
          : (data.value ?? "N/A"),
    },
    {
      label: historyInfo.labelComment ? historyInfo.labelComment : "Reason",
      colId: "reason",
      valueFormatter: (_, data) => (Boolean(data.reason) ? data.reason : "N/A"),
    },
    {
      label: "Updated By",
      colId: "createdBy",
      valueFormatter: (_, data) => `${data.creatorName} (${data.creatorRole})`,
    },
    {
      label: "Updated Date",
      colId: "createdDate",
      valueFormatter: function (value) {
        return moment(value).format("MM/DD/YYYY");
      },
    },
  ];

  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState<null | HTMLButtonElement>(null);
  const { data } = useSWR<(AnchorHistory & CoverageStatusHistory)[]>(
    !!anchorEl && accountKey
      ? `/api/dataservice/v1/accounts/${accountKey}/${historyInfo?.apiKey}/histories`
      : null
  );
  const popRef = useRef<HTMLDivElement>(null);
  const rootRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    document.addEventListener("click", closePop);
    return () => {
      document.removeEventListener("click", closePop);
    };
  }, []);
  const closePop = (e: Event) => {
    if (
      !popRef.current?.contains(e.target as Node) &&
      popRef.current !== e.target &&
      !rootRef.current?.contains(e.target as Node)
    ) {
      setAnchorEl(null);
    }
  };

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };

  return (
    <>
      <IconButton
        size="small"
        className={"hover-show block-svg"}
        onClick={handleClick}
        ref={rootRef}
      >
        <img
          src={"/detail/list-primary.svg"}
          className={"block-close" + accountKey}
        />
      </IconButton>
      <ArrowPopper
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        placement="right"
        disablePortal={false}
      >
        <div
          className={classes.listContainer}
          ref={popRef}
        >
          {data === undefined ? (
            <div className={classes.cardContainer}>
              {Array.from({ length: 3 }).map((_x, i) => (
                <div
                  className={classes.itemContainer}
                  key={i}
                >
                  <div className={classes.itemLabel}>
                    <Skeleton width={60} />
                  </div>
                  <div
                    className={classes.itemValue}
                    style={{ marginRight: 8 }}
                  >
                    <Skeleton width={80} />
                  </div>
                </div>
              ))}
            </div>
          ) : data.length === 0 ? (
            <p>No history yet</p>
          ) : (
            data.slice(0, 5).map((alert) => (
              <div
                className={classes.cardContainer}
                key={alert.createdDate}
              >
                {configs.map((config) => (
                  <div
                    className={classes.itemContainer}
                    key={config.label}
                  >
                    <div
                      className={classes.itemLabel}
                      style={{
                        width: 150,
                      }}
                    >
                      {config.label}
                      {config.tooltip ? (
                        <Tooltip
                          arrow={true}
                          placement="bottom"
                          title={config.tooltip}
                          classes={{ tooltip: classes.tooltip }}
                        >
                          <ErrorOutlineIcon className={classes.iconStyle} />
                        </Tooltip>
                      ) : null}
                    </div>
                    <div
                      className={classes.itemValue}
                      style={{ ...config?.valueStyle }}
                    >
                      {config.valueFormatter
                        ? config.valueFormatter(alert[config.colId] as string, alert)
                        : (alert[config.colId] as string)}
                    </div>
                  </div>
                ))}
              </div>
            ))
          )}
        </div>
      </ArrowPopper>
    </>
  );
};

export default classHOC(HistoryList);
